import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import type { PaginatedResponse } from "@/shared/http";
import type { SortDirection } from "@/types";

import {
  type ImageRegistryRecord,
  type ImageRepositoryRecord,
} from "../ImageRegistryRecord";

const imageRepositoriesKey = (orgId: string | undefined) =>
  ["imageRepositories", orgId] as const;

const repositoryImagesKey = (
  orgId: string | undefined,
  repositoryName: string,
) => ["imageRecords", "repository", orgId, repositoryName] as const;

export interface RepositoriesPageQuery {
  /** Name order. */
  sortDirection: SortDirection;
  /** Rows per page. */
  limit: number;
  /** The token the previous page handed out; none for the first page. */
  pageToken?: string;
}

/**
 * One page of the org's image repositories, as `query` asks for it. A changed
 * sort, page size or token is a new query, with the previous page standing in
 * until it lands.
 */
export function useImageRepositories(
  orgId: string | undefined,
  query: RepositoriesPageQuery,
) {
  const { imageRegistryService } = useDomainServices();

  return useQuery<PaginatedResponse<ImageRepositoryRecord>, Error>({
    queryKey: [...imageRepositoriesKey(orgId), query],
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("Requires an org"));
      }
      return imageRegistryService.getRepositoriesPage({
        orgId,
        abortSignal: signal,
        pageToken: query.pageToken,
        limit: query.limit,
        sortDirection: query.sortDirection,
      });
    },
    enabled: orgId !== undefined,
    placeholderData: keepPreviousData,
  });
}

/**
 * Every image in one repository. Images are read live from the registry, one
 * repository at a time, so fetch them for the repository a user opens rather
 * than for the whole org.
 */
export function useRepositoryImages(
  orgId: string | undefined,
  repositoryName: string,
) {
  const { imageRegistryService } = useDomainServices();
  return useQuery<ImageRegistryRecord[], Error>({
    queryKey: repositoryImagesKey(orgId, repositoryName),
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("Requires an org"));
      }
      return imageRegistryService.listImages(repositoryName, {
        orgId,
        abortSignal: signal,
      });
    },
    enabled: orgId !== undefined,
  });
}

/** Delete a repository and its images; the lists refetch on success. */
export function useDeleteImageRepository(orgId: string | undefined) {
  const { imageRegistryService } = useDomainServices();
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (repositoryName) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("Requires an org"));
      }
      return imageRegistryService.deleteRepository(repositoryName, { orgId });
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: imageRepositoriesKey(orgId),
        }),
        queryClient.invalidateQueries({ queryKey: ["imageRecords"] }),
      ]),
  });
}

/** Delete one image; its repository's image list refetches on success. */
export function useDeleteImage(orgId: string | undefined) {
  const { imageRegistryService } = useDomainServices();
  const queryClient = useQueryClient();
  return useMutation<void, Error, ImageRegistryRecord>({
    mutationFn: (image) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("Requires an org"));
      }
      return imageRegistryService.deleteImage(image.image_uri, { orgId });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        // Prefix: the per-repository lists and the org-wide one (`useImagesByOrg`).
        queryKey: ["imageRecords"],
      }),
  });
}
